import React = require("react");
import {VeboxDanfossLayout} from "../components/layout/VeboxDanfossLayout";
import {VeBoxLoginCard} from "../components/card/VeBoxLoginCard";
import {Display} from "../components/Display";
import {Button, useDebounce} from "@vebox/etui-core";
import {VeboxLoginInput} from "../components/card/VeboxLoginInput/VeboxLoginInput";
import "./ChangePasswordPage.scss";
import {RestDataLoader} from "../dashboard/widgets/unilever/RestDataLoader";
import {Loader} from "../components/Utils/loader/Loader";
import {isValidPassword} from "./AccountUtils";
import {ChangeCredentialsResponse} from "./Account.types";
import {veboxDefaultTheme} from "../index";

interface ChangePasswordPageTexts {
    changeCredentialsTitle: string;
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
    submit: string;
    passwordsAreDifferent: string;
    passwordNotSecure: string;
    changeCredentialsDescription: string;
    wrongOldPassword: string;
    passwordChanged: string;
    errorMessage: string;
    oldPasswordIdenticalToTheNewOne: string;
}

interface ChangePasswordPageProps {
    texts: ChangePasswordPageTexts;
}

type valuesToBeUpdated = {
    oldPassword: string,
    password: string,
    repeatPassword: string,
}

const enum valueType {
    oldPassword = "oldPassword",
    password = "password",
    repeatPassword = "repeatPassword"
}

export const ChangePasswordPage: React.FC<ChangePasswordPageProps> = ({texts}) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [values, setValues] = React.useState<valuesToBeUpdated>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isSubmittable, setIsSubmittable] = React.useState<boolean>(false);
    const [debouncedValues, setDebouncedValues] = useDebounce<valuesToBeUpdated>(values, 200);
    const [isDynamicCheck, setIsDynamicCheck] = React.useState<boolean>(false);
    const [success, setSuccess] = React.useState<boolean>();

    const updateTextInput = (name: valueType) => (e) => updateValue(name, e.target.value)
    const updateValue = (name: valueType, value: any) => {
        setValues(prevValues => ({
            ...prevValues,
            [name]: value
        }))
        setDebouncedValues(prevValues => ({
            ...prevValues,
            [name]: value
        }))
    }

    const validateSubmittable = () => {
        return (Boolean(debouncedValues)
            && (debouncedValues?.oldPassword !== undefined && debouncedValues?.oldPassword !== "")
            && (debouncedValues?.password !== undefined && debouncedValues?.password !== "")
            && (debouncedValues?.repeatPassword !== undefined && debouncedValues?.repeatPassword !== ""));
    }
    const validateCredentials = (): boolean => {
        if ((debouncedValues.password ?? "") !== (debouncedValues.repeatPassword ?? "")) {
            setErrorMessage(texts.passwordsAreDifferent);
            return false;

        } else if (!isValidPassword(debouncedValues.password)) {
            setErrorMessage(
                texts.passwordNotSecure);
            return false;

        } else if (debouncedValues.oldPassword === debouncedValues.password) {
            setErrorMessage(
                texts.oldPasswordIdenticalToTheNewOne);
            return false;
        } else {
            setErrorMessage("")
            return true
        }
    }

    React.useEffect(() => {
        let rightCredentials = true;
        if (isDynamicCheck) {
            rightCredentials = validateCredentials()
        }
        setIsSubmittable(validateSubmittable() && rightCredentials);
    }, [debouncedValues])

    const changeUserPassword = () => {
        if (isDynamicCheck ? isSubmittable : (isSubmittable && validateCredentials())) {
            setIsLoading(true)
            changePassword({oldPassword: values.oldPassword, password: values.password})
                .then(() => {
                    setSuccess(true);
                    setIsLoading(false);
                    setTimeout(() => window.location.href = "/vebox/login", 1000)
                })
                .catch((error) => {
                    switch (error.errorType) {
                        case "INVALID_PASSWORD": {
                            setErrorMessage(texts.wrongOldPassword);
                            break;
                        }
                        case "IDENTICAL_PASSWORD": {
                            setErrorMessage(texts.oldPasswordIdenticalToTheNewOne);
                            break;
                        }
                        default: {
                            setErrorMessage(texts.errorMessage);
                            console.error(error)
                            break;
                        }
                    }
                    setIsLoading(false);
                })
        } else {
            setIsDynamicCheck(true);
        }
    }


    return <VeboxDanfossLayout>
        <div className={"change-password-card-layout"}>
            <VeBoxLoginCard className={"change-password-card"}>
                <VeBoxLoginCard.Header
                    className={"change-password-card-header"}
                >
                    <VeBoxLoginCard.Title text={texts.changeCredentialsTitle}/>
                </VeBoxLoginCard.Header>
                <VeBoxLoginCard.Body className={"change-password-card-body"}>
                    <Display isVisible={!isLoading && success}>
                        <span className={"change-password-intro-text"}>
                            {texts.passwordChanged}
                        </span>
                    </Display>
                    <Display isVisible={!isLoading && !success}>
                    <span className={"change-password-intro-text"}>
                            {texts.changeCredentialsDescription}
                        </span>
                        <Display isVisible={errorMessage}>
                            <p className={"errorInfo"}>{errorMessage}</p>
                        </Display>
                        <VeboxLoginInput
                            type={"password"}
                            value={values?.oldPassword ?? ""}
                            placeholder={texts.oldPassword}
                            onChange={updateTextInput(valueType.oldPassword)}
                        />
                        <VeboxLoginInput
                            type={"password"}
                            value={values?.password ?? ""}
                            placeholder={texts.newPassword}
                            onChange={updateTextInput(valueType.password)}
                        />
                        <VeboxLoginInput
                            type={"password"}
                            value={values?.repeatPassword ?? ""}
                            placeholder={texts.confirmNewPassword}
                            onChange={updateTextInput(valueType.repeatPassword)}
                        />
                    </Display>
                    <Display isVisible={isLoading}>
                        <div className={"loader-section"}>
                            <Loader color={veboxDefaultTheme.palette.brand.main}/>
                        </div>
                    </Display>
                </VeBoxLoginCard.Body>
                <VeBoxLoginCard.Footer className={"change-password-card-footer"}>
                    <Display isVisible={!isLoading && !success}>
                        <Button
                            disabled={!isSubmittable}
                            variant={"strong"}
                            onClick={changeUserPassword}
                        >
                            {texts?.submit}
                        </Button>
                    </Display>
                </VeBoxLoginCard.Footer>
            </VeBoxLoginCard>
        </div>
    </VeboxDanfossLayout>
}

interface ChangePasswordPageParam {
    password: string,
    oldPassword: string
}

const changePassword = (params: ChangePasswordPageParam): Promise<ChangeCredentialsResponse> => {
    return new Promise<ChangeCredentialsResponse>((resolve, reject) => {
        RestDataLoader.putJSON(
            "users/current/credentials",
            params,
            resolve,
            reject
        )
    })
}